<template>
  <div
    class="playing-card"
    :class="[
      `suit-${card.suit.name}`,
      size,
      { 'is-red': card.suit.color === 'red', 'is-black': card.suit.color === 'black', 'selected': selected, 'face-down': faceDown, 'is-mobile': isMobile }
    ]"
    @click="handleClick"
    @touchstart="handleTouchStart"
    @dblclick="handleDblclick"
  >
    <template v-if="!faceDown">
      <div class="card-front-sprite" :style="cardSpriteStyle"></div>
    </template>
    <template v-else>
      <div class="card-back"></div>
    </template>
  </div>
</template>

<script>
/**
 * 扑克牌组件。
 *
 * 正面牌从 assets/images/card.png 图集中裁切。
 * 图集为 6 列 × 9 行，单格 148×210，整图 1024×1890（右侧留白）。
 * 黑桃、红桃按点数连续排列；梅花、方块在后四行交错，位置见 CARD_CELLS。
 * 第 1 行前两格分别是大鬼、小鬼。
 */
const CELL_W = 148
const CELL_H = 210
const IMG_W = 1024
const IMG_H = 1890
const CARD_SPRITE = require('@/assets/images/card.png')

// rankIndex: 0=A, 1=2, ... 9=10, 10=J, 11=Q, 12=K → [col, row]
const CARD_CELLS = {
  spades: [
    [2, 0], [3, 0], [4, 0], [5, 0],
    [0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [5, 1],
    [0, 2], [1, 2], [2, 2]
  ],
  hearts: [
    [3, 2], [4, 2], [5, 2],
    [0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3],
    [0, 4], [1, 4], [2, 4], [3, 4]
  ],
  clubs: [
    [4, 4], [5, 4], [0, 5], [0, 6], [0, 7], [0, 8],
    [1, 5], [1, 6], [1, 7], [1, 8],
    [2, 5], [3, 5], [4, 5]
  ],
  diamonds: [
    [5, 5], [2, 6], [2, 7], [2, 8],
    [3, 6], [4, 6], [5, 6],
    [3, 7], [3, 8], [4, 7],
    [5, 7], [4, 8], [5, 8]
  ]
}

function cardSpriteStyle(col, row) {
  return {
    backgroundImage: `url(${CARD_SPRITE})`,
    backgroundSize: `${(IMG_W / CELL_W) * 100}% ${(IMG_H / CELL_H) * 100}%`,
    backgroundPosition: `${(col * CELL_W) / (IMG_W - CELL_W) * 100}% ${(row * CELL_H) / (IMG_H - CELL_H) * 100}%`
  }
}

export default {
  name: 'PlayingCard',
  data() {
    return {
      isMobile: false,
      lastTapTime: 0
    }
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  },
  computed: {
    rankValue() {
      return Number(this.card.rank.value)
    },
    cardSpriteStyle() {
      // 第 1 行：col 0=大鬼, col 1=小鬼
      if (this.card.suit.name === 'joker') {
        const colIndex = this.card.jokerType === 'small' ? 1 : 0
        return cardSpriteStyle(colIndex, 0)
      }

      const rankIndex = this.rankValue === 1 ? 0 : this.rankValue - 1
      const cell = (CARD_CELLS[this.card.suit.name] || CARD_CELLS.spades)[rankIndex]
      return cardSpriteStyle(cell[0], cell[1])
    }
  },
  methods: {
    handleClick(e) {
      if (this.isMobile) return
      e.stopPropagation()
      this.$emit('click', this.card)
    },
    handleTouchStart(e) {
      if (!this.isMobile) return
      e.stopPropagation()
      e.preventDefault()

      const now = Date.now()
      const timeDiff = now - this.lastTapTime

      if (timeDiff < 250) {
        // 双击：移回手牌区
        this.$emit('remove', this.card)
      } else {
        // 单击：选中/取消选中
        this.$emit('click', this.card)
      }

      this.lastTapTime = now
    },
    handleDblclick(e) {
      if (this.isMobile) return
      this.$emit('remove', this.card)
    }
  },
  props: {
    card: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    faceDown: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal', // 'normal'、'small' 或 'tiny'
      validator: value => ['normal', 'small', 'tiny'].includes(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.playing-card {
  width: 68px;
  height: 94px;
  background: white;
  border-radius: 9px;
  position: relative;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  touch-action: manipulation;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &.selected {
    transform: translateY(-7px);
    box-shadow: 0 0 0 3px #ffd700, 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &.is-red {
    color: #e74c3c;
  }

  &.is-black {
    color: #2c3e50;
  }

  &.face-down {
    background: linear-gradient(135deg, #1e5799 0%, #2989d8 50%, #207cca 51%, #7db9e8 100%);

    .card-back {
      width: 100%;
      height: 100%;
      background: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.1),
        rgba(255, 255, 255, 0.1) 5px,
        transparent 5px,
        transparent 10px
      );
      border-radius: 9px;
    }
  }

  // 小尺寸样式
  &.small {
    width: 54px;
    height: 75px;
    border-radius: 6px;

    .card-corner {
      font-size: 12px;

      .rank {
        font-size: 15px;
      }

      .suit {
        font-size: 9px;
      }

      &.top-left {
        top: 3px;
        left: 3px;
      }

      &.bottom-right {
        bottom: 3px;
        right: 3px;
      }
    }

    .card-center .suit-large {
      font-size: 27px;
    }

    .card-doll {
      width: 46px;
      height: 66px;
      transform: scale(0.82);
      transform-origin: bottom right;
    }

    &:hover {
      transform: translateY(-2px);
    }

    &.selected {
      transform: translateY(-5px);
    }
  }

  // 超小尺寸样式（其他玩家牌缩小 30%）
  &.tiny {
    width: 38px;
    height: 53px;
    border-radius: 4px;

    .card-corner {
      font-size: 8px;

      .rank {
        font-size: 10px;
      }

      .suit {
        font-size: 6px;
      }

      &.top-left {
        top: 2px;
        left: 2px;
      }

      &.bottom-right {
        bottom: 2px;
        right: 2px;
      }
    }

    .card-center .suit-large {
      font-size: 19px;
    }

    .card-doll {
      width: 46px;
      height: 66px;
      transform: scale(0.58);
      transform-origin: bottom right;
    }

    &:hover {
      transform: translateY(-1px);
    }

    &.selected {
      transform: translateY(-3px);
    }
  }
}

.card-front-sprite {
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background-repeat: no-repeat;
  background-color: #fff;
}

.card-corner {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;

  &.top-left {
    top: 5px;
    left: 5px;
  }

  &.bottom-right {
    bottom: 5px;
    right: 5px;
    transform: rotate(180deg);
  }

  .rank {
    font-size: 16px;
  }

  .suit {
    font-size: 11px;
  }
}

.card-center {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .suit-large {
    font-size: 36px;
  }
}

.card-doll {
  position: absolute;
  right: 1px;
  bottom: 1px;
  width: 46px;
  height: 66px;
  color: inherit;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.25));

  .doll-crown {
    position: absolute;
    top: 3px;
    left: 18px;
    width: 25px;
    height: 14px;
    border: 1px solid #2c2c2c;
    border-bottom-width: 2px;
    background: linear-gradient(180deg, #ffd54a 0%, #f3a900 100%);
    clip-path: polygon(0 100%, 12% 30%, 28% 100%, 50% 10%, 72% 100%, 88% 30%, 100% 100%);
    z-index: 4;
  }

  .doll-head {
    position: absolute;
    top: 14px;
    left: 16px;
    width: 25px;
    height: 25px;
    border-radius: 48% 48% 42% 42%;
    background:
      radial-gradient(circle at 64% 44%, #2c2c2c 0 1.5px, transparent 1.7px),
      linear-gradient(90deg, transparent 0 64%, rgba(0, 0, 0, 0.18) 64% 68%, transparent 68%),
      linear-gradient(180deg, #ffe2b8 0%, #f3b978 100%);
    border: 1px solid #2b2b2b;
    z-index: 3;

    &::before {
      content: '';
      position: absolute;
      left: -8px;
      top: 2px;
      width: 14px;
      height: 24px;
      border-radius: 10px 2px 8px 10px;
      background: #f4d33c;
      border-left: 1px solid #2b2b2b;
      transform: rotate(-8deg);
    }

    &::after {
      content: '';
      position: absolute;
      left: 17px;
      top: 15px;
      width: 8px;
      height: 5px;
      border-bottom: 2px solid #9b2424;
      border-radius: 0 0 8px 8px;
    }
  }

  .doll-body {
    position: absolute;
    right: 1px;
    bottom: 0;
    width: 39px;
    height: 38px;
    border-radius: 26px 8px 4px 4px;
    background:
      linear-gradient(112deg, transparent 0 23%, #ffd34d 23% 36%, transparent 36%),
      radial-gradient(circle at 45% 58%, rgba(255, 255, 255, 0.9) 0 8px, transparent 8.5px),
      linear-gradient(135deg, currentColor 0%, currentColor 58%, #20242a 58% 100%);
    border: 1px solid #2b2b2b;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      left: 4px;
      top: 15px;
      width: 28px;
      height: 8px;
      border-radius: 10px;
      background: #ffd34d;
      border: 1px solid rgba(43, 43, 43, 0.75);
      transform: rotate(-36deg);
    }
  }

  .doll-suit {
    position: absolute;
    right: 7px;
    bottom: 7px;
    font-size: 13px;
    line-height: 1;
    color: currentColor;
    background: rgba(255, 255, 255, 0.82);
    border-radius: 50%;
    padding: 1px 2px;
    z-index: 2;
  }

  .doll-label {
    position: absolute;
    left: 10px;
    bottom: 6px;
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    text-align: center;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.65);
    z-index: 3;
  }

  &.queen-doll {
    .doll-crown {
      top: 6px;
      height: 9px;
      clip-path: none;
      border-radius: 2px;
      background:
        repeating-linear-gradient(90deg, #fff3a6 0 5px, #e6a900 5px 8px);
    }

    .doll-head::before {
      background: #9a6a34;
      height: 28px;
      width: 16px;
    }

    .doll-body {
      background:
        linear-gradient(112deg, transparent 0 23%, #ffd34d 23% 36%, transparent 36%),
        radial-gradient(circle at 45% 58%, rgba(255, 255, 255, 0.9) 0 8px, transparent 8.5px),
        linear-gradient(135deg, currentColor 0%, currentColor 72%, #d8a23a 72% 100%);
    }
  }

  &.jack-doll {
    .doll-crown {
      top: 7px;
      left: 15px;
      width: 31px;
      height: 9px;
      border-radius: 12px 12px 2px 2px;
      clip-path: none;
      background:
        linear-gradient(180deg, #1d2530 0 45%, #f0c83e 45% 70%, #1d2530 70% 100%);
    }

    .doll-head::before {
      background: #f0c83e;
      height: 26px;
    }

    .doll-body {
      background:
        linear-gradient(112deg, transparent 0 23%, #ffd34d 23% 36%, transparent 36%),
        radial-gradient(circle at 45% 58%, rgba(255, 255, 255, 0.9) 0 8px, transparent 8.5px),
        linear-gradient(135deg, currentColor 0%, currentColor 52%, #20242a 52% 100%);
    }
  }
}

// 移动端样式（通过 JS 类名触发，避免 @media HMR 缓存问题）
.playing-card.is-mobile {
  width: 50px;
  height: 70px;
  border-radius: 7px;

  .card-corner {
    font-size: 12px;

    .rank {
      font-size: 14px;
    }

    .suit {
      font-size: 7px;
    }

    &.top-left {
      top: 4px;
      left: 4px;
    }

    &.bottom-right {
      bottom: 4px;
      right: 4px;
    }
  }

  .card-center .suit-large {
    font-size: 26px;
  }

  .card-doll {
    transform: scale(0.78);
    transform-origin: bottom right;
  }

  &.small {
    width: 76px;
    height: 106px;
    border-radius: 10px;

    .card-corner {
      font-size: 18px;

      .rank {
        font-size: 22px;
      }

      .suit {
        font-size: 10px;
      }

      &.top-left {
        top: 6px;
        left: 6px;
      }

      &.bottom-right {
        bottom: 6px;
        right: 6px;
      }
    }

    .card-center .suit-large {
      font-size: 38px;
    }

    .card-doll {
      transform: scale(1.18);
      transform-origin: bottom right;
    }
  }

  &.tiny {
    width: 60px;
    height: 84px;
    border-radius: 8px;

    .card-corner {
      font-size: 12px;

      .rank {
        font-size: 16px;
      }

      .suit {
        font-size: 8px;
      }

      &.top-left {
        top: 4px;
        left: 4px;
      }

      &.bottom-right {
        bottom: 4px;
        right: 4px;
      }
    }

    .card-center .suit-large {
      font-size: 28px;
    }

    .card-doll {
      transform: scale(0.92);
      transform-origin: bottom right;
    }
  }

  // 结算页面保持原始尺寸，不放大
  &.settle-mode {
    &.small {
      width: 38px;
      height: 53px;
      border-radius: 5px;

      .card-corner {
        font-size: 9px;

        .rank {
          font-size: 11px;
        }

        .suit {
          font-size: 5px;
        }

        &.top-left {
          top: 3px;
          left: 3px;
        }

        &.bottom-right {
          bottom: 3px;
          right: 3px;
        }
      }

      .card-center .suit-large {
        font-size: 19px;
      }

      .card-doll {
        transform: scale(0.56);
        transform-origin: bottom right;
      }
    }

    &.tiny {
      width: 30px;
      height: 42px;
      border-radius: 4px;

      .card-corner {
        font-size: 6px;

        .rank {
          font-size: 8px;
        }

        .suit {
          font-size: 4px;
        }

        &.top-left {
          top: 2px;
          left: 2px;
        }

        &.bottom-right {
          bottom: 2px;
          right: 2px;
        }
      }

      .card-center .suit-large {
        font-size: 14px;
      }

      .card-doll {
        transform: scale(0.42);
        transform-origin: bottom right;
      }
    }
  }
}
</style>
